import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMaintenanceTasks, saveMaintenanceTask, deleteMaintenanceTask, GetMaintenanceTasksOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Wrench, Plus, AlertCircle, CheckCircle2, Clock, Loader2, Pencil, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Task = GetMaintenanceTasksOutputType['tasks'][0];

const CATEGORIES = ['Plumbing', 'Electrical', 'Equipment', 'Structural', 'Pest', 'Cleaning', 'IT', 'Cellar', 'Garden', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const PRIORITY_COLORS = { Urgent: 'bg-red-500/10 text-red-700 border-red-500/30', High: 'bg-orange-500/10 text-orange-700 border-orange-500/30', Medium: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', Low: 'bg-muted text-muted-foreground' };
const STATUS_CONFIG = { Open: { icon: AlertCircle, cls: 'text-blue-600' }, 'In Progress': { icon: Clock, cls: 'text-yellow-600' }, Resolved: { icon: CheckCircle2, cls: 'text-green-600' }, "Won't Fix": { icon: AlertCircle, cls: 'text-muted-foreground' } };
const fmtDt = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy, HH:mm') : ''; } catch { return ''; } };

const BLANK = { title: '', description: '', location: '', category: '', priority: 'Medium' };

export default function Maintenance() {
  const { user } = useAuth();
  const isManager = user?.role === 'Manager' || user?.role === 'Admin';

  const [tasks, setTasks] = useState<Task[]>([]);
  const [counts, setCounts] = useState({ open: 0, inProgress: 0, resolved: 0, urgent: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Task | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editAssigned, setEditAssigned] = useState('');
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('Active');

  const load = () => {
    getMaintenanceTasks({}).then(d => { setTasks(d.tasks); setCounts(d.counts); }).finally(() => setLoading(false));
  };
  useEffect(() => { if (!user) return; load(); }, [user]);

  const displayed = filterStatus === 'Active'
    ? tasks.filter(t => t.status === 'Open' || t.status === 'In Progress')
    : filterStatus === 'Mine'
    ? tasks.filter(t => t.reportedById === user?.id)
    : filterStatus === 'Resolved'
    ? tasks.filter(t => t.status === 'Resolved' || t.status === "Won't Fix")
    : tasks;

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Please add a title'); return; }
    setSaving(true);
    try {
      await saveMaintenanceTask({ ...form, priority: form.priority || 'Medium' });
      toast.success('Issue reported — thank you!');
      setForm({ ...BLANK });
      setShowForm(false);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const openTask = (task: Task) => {
    setSelected(task);
    setEditStatus(task.status || 'Open');
    setEditNotes(task.resolutionNotes || '');
    setEditAssigned(task.assignedTo || '');
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setSavingUpdate(true);
    try {
      await saveMaintenanceTask({ id: selected.id, title: selected.title, status: editStatus, resolutionNotes: editNotes, assignedTo: editAssigned });
      toast.success('Updated');
      setSelected(null);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSavingUpdate(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteMaintenanceTask({ id }); toast.success('Deleted'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Wrench className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Maintenance Log</h1>
                <p className="text-sm text-muted-foreground">Report issues and track repairs around the pub</p>
              </div>
            </div>
            <Button className="gap-1.5" onClick={() => setShowForm(s => !s)}>
              <Plus className="w-4 h-4" /> Report Issue
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Open', value: counts.open, cls: 'text-blue-600' },
            { label: 'In Progress', value: counts.inProgress, cls: 'text-yellow-600' },
            { label: 'Urgent', value: counts.urgent, cls: 'text-red-600' },
            { label: 'Resolved', value: counts.resolved, cls: 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="border border-border rounded-xl p-4 text-center bg-card">
              <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Report form */}
        {showForm && (
          <div className="border border-primary/30 bg-primary/5 rounded-xl p-5 space-y-3">
            <p className="font-semibold text-foreground">Report a Maintenance Issue</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Title / Brief Description *</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Tap dripping in gents toilet" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Category</label>
                <Select value={form.category || 'none'} onValueChange={v => setForm(f => ({ ...f, category: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not sure</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Location</label>
                <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Gents toilet, main bar, kitchen" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Full Description</label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} placeholder="Describe the problem in detail..." />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saving} className="gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {saving ? 'Submitting...' : 'Submit Report'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          {['Active', 'Mine', 'Resolved', 'All'].map(f => (
            <button key={f} onClick={() => setFilterStatus(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-18" />)}</div>
        ) : displayed.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
            <Wrench className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No issues {filterStatus === 'Active' ? 'open' : filterStatus === 'Mine' ? "you've reported" : 'found'}</p>
            <p className="text-xs mt-1">Use the "Report Issue" button above to log a problem.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayed.map(task => {
              const cfg = STATUS_CONFIG[task.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.Open;
              const StatusIcon = cfg.icon;
              const canEdit = isManager || task.reportedById === user?.id;
              return (
                <div key={task.id} className="border border-border bg-card rounded-xl p-4 flex items-start gap-3 hover:bg-muted/10 transition-colors">
                  <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.cls}`} />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openTask(task)}>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-foreground text-sm">{task.title}</span>
                      {task.priority && <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority as keyof typeof PRIORITY_COLORS] || ''}`}>{task.priority}</Badge>}
                      <Badge variant="outline" className="text-xs">{task.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {task.category && <span>{task.category} · </span>}
                      {task.location && <span>{task.location} · </span>}
                      {task.reporterName && <span>by {task.reporterName} · </span>}
                      {fmtDt(task.reportedAt)}
                    </p>
                    {task.assignedTo && <p className="text-xs text-primary mt-0.5">→ {task.assignedTo}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openTask(task)}><Pencil className="w-3.5 h-3.5" /></Button>
                      {isManager && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete this task?</AlertDialogTitle></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(task.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Task detail sheet */}
      <Sheet open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          {selected && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle>{selected.title}</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2 bg-muted/30 rounded-lg p-3">
                  {selected.category && <div><span className="text-xs text-muted-foreground block">Category</span>{selected.category}</div>}
                  {selected.priority && <div><span className="text-xs text-muted-foreground block">Priority</span>{selected.priority}</div>}
                  {selected.location && <div><span className="text-xs text-muted-foreground block">Location</span>{selected.location}</div>}
                  {selected.reporterName && <div><span className="text-xs text-muted-foreground block">Reported By</span>{selected.reporterName}</div>}
                  {selected.reportedAt && <div className="col-span-2"><span className="text-xs text-muted-foreground block">Reported At</span>{fmtDt(selected.reportedAt)}</div>}
                </div>
                {selected.description && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Description</p>
                    <p className="text-sm text-foreground leading-relaxed">{selected.description}</p>
                  </div>
                )}
                {(isManager || selected.reportedById === user?.id) && (
                  <>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                      <Select value={editStatus} onValueChange={setEditStatus}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Open">Open</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Resolved">Resolved</SelectItem>
                          <SelectItem value="Won't Fix">Won't Fix</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {isManager && (
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Assigned To</label>
                        <Input value={editAssigned} onChange={e => setEditAssigned(e.target.value)} placeholder="Name or contractor" />
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Resolution Notes</label>
                      <Textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3} placeholder="What was done to fix this..." />
                    </div>
                    <Button className="w-full" onClick={handleUpdate} disabled={savingUpdate}>
                      {savingUpdate ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
